/**
 * Pull the sentences a page actually makes as claims about the thing it sells.
 *
 * Deliberately mechanical and model-free: this runs on the raw text the
 * Resolve step already fetched, so the count reported back to the user
 * ("12 claims") is a count of real sentences from their page, not a model's
 * impression of one. Everything downstream quotes these verbatim.
 */

import {
  cleanSourceText,
  findPageHeadlines,
  looksLikePageFurniture,
  splitSentences,
} from './clean-source-text';

import { findResultTiles, isResultClaim } from '@/lib/content-doctrine/result-evidence';
import { rankResultsByStrength } from '@/lib/content-doctrine/result-strength';
import {
  CLAIM_NOISE,
  MAX_CLAIM_CHARS,
  MIN_CLAIM_CHARS,
  looksLikeClaim,
  normalizeForCompare,
} from '@/lib/paid-media/checks/source-claims';

const isNoise = (sentence: string): boolean => CLAIM_NOISE.some((re) => re.test(sentence));

export interface ExtractClaimsOptions {
  /** The page's own title. A sentence that restates it is a headline, not a claim. */
  title?: string | null;
  /**
   * The page is the brand's OWN site (`isBrandOwnedSource`), so a results tile
   * and a first-person result sentence on it are the brand's client results.
   * Default false: a figure on somebody else's page is never a brand result, so
   * the tile is not stitched and a "we/our clients saved $X" sentence is dropped.
   */
  brandOwned?: boolean;
}

export function extractClaims(
  text: string,
  limit = 20,
  options: ExtractClaimsOptions = {},
): string[] {
  if (!text) return [];

  const cleaned = cleanSourceText(text);
  const normalizedTitle = options.title ? normalizeForCompare(options.title) : '';
  const normalizedWhole = normalizeForCompare(cleaned);
  const normalizedHeadlines = findPageHeadlines(text)
    .map(normalizeForCompare)
    .filter((headline) => headline.length >= 20);

  const seen = new Set<string>();
  // A results tile ("Data Centers. $5M. Tax Savings. Texas.") is written as
  // stacked fragments, each too short to be a sentence, so the loop below would
  // drop every one of them and the strongest evidence on a services page, its
  // quantified client results, would never reach an angle or an ad. Each tile
  // is stitched back into one verbatim claim and goes first, so the claim
  // limit can never push it out behind FAQ answers.
  const claims: string[] = [];
  const brandOwned = options.brandOwned === true;
  // The brand's own client results are lifted ahead of every other claim and
  // ranked by strength (`rankResultsByStrength`), so the claim limit can never
  // cut one: a results sentence at the foot of a page ("ITC Tax has documented
  // ... a $6 million annual tax reduction representing a 40% decrease") sat past
  // a 20-claim cap behind FAQ answers and never reached an angle. A tile is
  // stitched from stacked fragments; a result sentence is taken whole.
  if (brandOwned) {
    const results: string[] = [];
    for (const tile of findResultTiles(cleaned)) {
      const normalized = normalizeForCompare(tile);
      if (seen.has(normalized)) continue;
      seen.add(normalized);
      results.push(tile);
    }
    for (const raw of splitSentences(cleaned)) {
      const sentence = raw.replace(/\s+/g, ' ').trim();
      if (sentence.length < MIN_CLAIM_CHARS || sentence.length > MAX_CLAIM_CHARS) continue;
      if (isNoise(sentence) || looksLikePageFurniture(sentence) || !looksLikeClaim(sentence)) {
        continue;
      }
      if (!isResultClaim(sentence)) continue;
      const normalized = normalizeForCompare(sentence);
      if (seen.has(normalized)) continue;
      seen.add(normalized);
      results.push(sentence);
    }
    for (const result of rankResultsByStrength(results)) {
      claims.push(result);
      if (claims.length >= limit) return claims;
    }
  }

  for (const raw of splitSentences(cleaned)) {
    const sentence = raw.replace(/\s+/g, ' ').trim();
    if (sentence.length < MIN_CLAIM_CHARS || sentence.length > MAX_CLAIM_CHARS) continue;
    if (isNoise(sentence)) continue;
    if (looksLikePageFurniture(sentence)) continue;
    if (!looksLikeClaim(sentence)) continue;
    if (!brandOwned && isResultClaim(sentence)) continue;

    const normalized = normalizeForCompare(sentence);
    if (normalizedTitle.length >= 20 && normalized.includes(normalizedTitle)) continue;
    if (normalizedHeadlines.some((headline) => normalized.includes(headline))) continue;
    // A headline is printed once as the title and again inside the page's own
    // share/republish widget; a sentence whose opening appears twice in the
    // page is that repeated furniture, not a claim the article makes.
    const opening = normalized.slice(0, 60);
    if (
      opening.length >= 40 &&
      normalizedWhole.split(opening).length - 1 >= 2 &&
      normalizedWhole.split(normalized).length - 1 <= 1
    ) {
      continue;
    }

    if (seen.has(normalized)) continue;
    seen.add(normalized);

    claims.push(sentence);
    if (claims.length >= limit) break;
  }

  return claims;
}

/**
 * The claim list for a Brief that read several pages. Results from EVERY page
 * the brand owns are ranked together by strength and lead; after them come the
 * primary page's remaining claims, then each extra page's, de-duplicated. The
 * primary page therefore still outranks an extra page for everything that is
 * not a result, but a stronger result on the second page (the data-centers
 * page's "$6 million annual tax reduction ... 40% decrease") is no longer
 * placed behind the first page's weaker ones just because it was read second.
 */
export function mergeSourceClaims(
  primary: readonly string[],
  extras: readonly (readonly string[])[],
): string[] {
  const all: string[] = [];
  const seen = new Set<string>();
  for (const claim of [...primary, ...extras.flat()]) {
    const key = normalizeForCompare(claim);
    if (seen.has(key)) continue;
    seen.add(key);
    all.push(claim);
  }
  const results = rankResultsByStrength(all.filter(isResultClaim));
  const resultSet = new Set(results);
  return [...results, ...all.filter((claim) => !resultSet.has(claim))];
}
